<template>
  <PageHeader
    :breadcrumRoutes="ROUTES"
    title="Formulario de traspaso de póliza"
  />

  <a-form name="form" layout="vertical" ref="formRef" :model="formData">
    <a-row :gutter="[8, 8]">
      <a-col :xs="24">
        <a-card>
          <a-typography-title class="text-gray-8" :level="4">
            Datos generales
          </a-typography-title>

          <a-row :gutter="[8, 8]">
            <a-col :xs="24">
              <a-form-item
                name="formType"
                label="Envío de información a"
                :rules="{ required: true }"
              >
                <a-radio-group value="1">
                  <a-radio value="1">Compañía</a-radio>
                </a-radio-group>
              </a-form-item>
            </a-col>
            <a-col :xs="24" :md="8">
              <a-form-item
                name="rut"
                label="RUT"
                :rules="{ required: true, message: 'RUT es requerido' }"
                :validateStatus="employeeError ? 'error' : ''"
                :help="employeeError"
              >
                <InputRut
                  :disabled="fetchingEmployee"
                  placeholder="RUT"
                  v-model="formData.rut"
                />
              </a-form-item>
            </a-col>
            <a-col :xs="24" :md="8">
              <a-form-item
                name="fullname"
                label="Nombre completo"
                :rules="{ required: true }"
              >
                <InputRut
                  disabled
                  placeholder="..."
                  v-model="formData.fullname"
                />
              </a-form-item>
            </a-col>
            <a-col :xs="24" :md="8">
              <a-form-item
                name="startDate"
                label="Fecha de inicio de vigencia"
                :rules="{ required: true }"
              >
                <a-date-picker
                  style="width: 100%"
                  placeholder="DD-MM-AAAA"
                  v-model:value="formData.startDate"
                  :format="DATE_FORMAT"
                />
              </a-form-item>
            </a-col>
          </a-row>
        </a-card>
      </a-col>

      <a-col :xs="24">
        <a-card>
          <a-typography-title class="text-gray-8" :level="4">
            Datos póliza de origen
          </a-typography-title>

          <a-row :gutter="8">
            <a-col :xs="24" :md="12">
              <a-form-item
                name="originCompanyId"
                label="Empresa"
                :rules="{ required: true }"
                :validateStatus="originCompaniesError ? 'error' : ''"
                :help="originCompaniesError"
              >
                <SelectOrdered
                  :loading="fetchingOriginCompanies"
                  :disabled="!originCompanies.length || fetchingOriginCompanies"
                  placeholder="Seleccionar empresa de origen"
                  v-model="formData.originCompanyId"
                  :options="originCompanyOptions"
                />
              </a-form-item>
            </a-col>
            <a-col :xs="24" :md="12">
              <a-form-item
                ref="originInsuranceCompanyId"
                label="Aseguradora"
                name="originInsuranceCompanyId"
                :rules="{ required: true }"
                :validateStatus="originInsuranceCompaniesError ? 'error' : ''"
                :help="originInsuranceCompaniesError"
              >
                <SelectOrdered
                  :loading="fetchingOriginInsurances"
                  :disabled="
                    !originInsuranceCompanies.length || fetchingOriginInsurances
                  "
                  placeholder="Seleccionar aseguradora de origen"
                  v-model="formData.originInsuranceCompanyId"
                  :options="originInsuranceOptions"
                />
              </a-form-item>
            </a-col>
            <a-col
              :xs="24"
              v-if="
                formData.originCompanyId && formData.originInsuranceCompanyId
              "
            >
              <a-form-item
                label="Pólizas"
                name="originPolicyIds"
                :rules="{ required: true }"
                :validateStatus="originPoliciesError ? 'error' : ''"
                :help="originPoliciesError"
              >
                <SelectOrdered
                  :loading="fetchingOriginPolicies"
                  :disabled="!originPolicies.length || fetchingOriginPolicies"
                  placeholder="Seleccionar pólizas de origen"
                  v-model="formData.originPolicyIds"
                  :options="originPolicyOptions"
                  mode="multiple"
                />
              </a-form-item>
            </a-col>
            <a-col
              v-for="(policy, index) in formData.originPolicies"
              :key="index"
              :xs="24"
              :md="8"
            >
              <a-form-item
                :name="['originPolicies', index, 'originGroupIds']"
                :label="`Grupos de póliza ${policy.numberPolicy}`"
                :rules="{ required: true }"
              >
                <SelectOrdered
                  placeholder="Seleccionar grupos de póliza de origen"
                  v-model="policy.originGroupIds"
                  :options="
                    policy.originGroups.map((group) => ({
                      label: group.name,
                      value: group.id
                    }))
                  "
                  mode="multiple"
                />
              </a-form-item>
            </a-col>
          </a-row>
        </a-card>
      </a-col>

      <a-col :xs="24">
        <a-card>
          <a-typography-title class="text-gray-8" :level="4">
            Datos póliza de destino
          </a-typography-title>

          <a-row :gutter="[8, 8]">
            <a-col :xs="24" :md="12">
              <a-form-item
                label="Empresa"
                name="destinyCompanyId"
                :rules="{ required: true }"
                :validateStatus="destinyCompaniesError ? 'error' : ''"
                :help="destinyCompaniesError"
              >
                <SelectOrdered
                  :loading="fetchingDestinyCompanies"
                  :disabled="
                    !destinyCompanies.length || fetchingDestinyCompanies
                  "
                  placeholder="Seleccionar empresa de destino"
                  v-model="formData.destinyCompanyId"
                  :options="destinyCompanyOptions"
                />
              </a-form-item>
            </a-col>
            <a-col :xs="24" :md="12">
              <a-form-item
                label="Aseguradora"
                name="destinyInsuranceCompanyId"
                :rules="{ required: true }"
                :validateStatus="destinyInsuranceCompaniesError ? 'error' : ''"
                :help="destinyInsuranceCompaniesError"
              >
                <SelectOrdered
                  :loading="fetchingDestinyInsuranceCompanies"
                  :disabled="
                    !destinyInsuranceCompanies.length ||
                    fetchingDestinyInsuranceCompanies
                  "
                  placeholder="Seleccionar aseguradora de destino"
                  v-model="formData.destinyInsuranceCompanyId"
                  :options="destinyInsuranceCompanyOptions"
                />
              </a-form-item>
            </a-col>
            <a-col
              :xs="24"
              v-if="
                formData.destinyCompanyId && formData.destinyInsuranceCompanyId
              "
            >
              <a-form-item
                label="Pólizas"
                name="destinyPolicyIds"
                :rules="{ required: true }"
                :validateStatus="destinyPoliciesError ? 'error' : ''"
                :help="destinyPoliciesError"
              >
                <SelectOrdered
                  :loading="fetchingDestinyPolicies"
                  :disabled="!destinyPolicies.length || fetchingDestinyPolicies"
                  placeholder="Seleccionar pólizas de destino"
                  mode="multiple"
                  v-model="formData.destinyPolicyIds"
                  :options="destinyPolicyOptions"
                />
              </a-form-item>
            </a-col>
          </a-row>

          <a-row :gutter="[8, 8]">
            <a-col
              v-for="(policy, index) in formData.destinyPolicies"
              :key="index"
              :xs="24"
              :md="8"
            >
              <a-form-item
                :name="['destinyPolicies', index, 'destinyGroupIds']"
                :label="`Grupos de póliza ${policy.numberPolicy}`"
                :rules="{ required: true }"
              >
                <SelectOrdered
                  @change="
                    (e) => changeDestinyGroupCompany(e, policy.destinyGroups)
                  "
                  v-model="policy.destinyGroupIds"
                  :options="
                    policy.destinyGroups.map((group) => ({
                      label: group.name,
                      value: group.id
                    }))
                  "
                  mode="multiple"
                />
              </a-form-item>
            </a-col>
          </a-row>

          <template
            :key="policy.id"
            v-for="(policy, i) in formData.destinyPolicies"
          >
            <a-row v-if="policy.destinyGroupIds.length > 0" :gutter="[8, 8]">
              <a-col :span="24">
                <a-typography-title class="text-gray-8" :level="5">
                  Coberturas de póliza {{ policy.numberPolicy }}
                </a-typography-title>
              </a-col>
              <template
                :key="group.id"
                v-for="(group, j) in policy.destinyGroups"
              >
                <a-col
                  v-if="policy.destinyGroupIds.includes(group.id)"
                  :xs="24"
                  :md="8"
                >
                  <a-form-item
                    :name="[
                      'destinyPolicies',
                      i,
                      'destinyGroups',
                      j,
                      'destinyCoverageIds'
                    ]"
                    :label="`Grupo ${group.name}`"
                    :rules="{ required: true }"
                  >
                    <SelectOrdered
                      v-model="policy.destinyGroups[j].destinyCoverageIds"
                      :options="
                        policy.destinyGroups[j].destinyCoverages.map((cgc) => ({
                          label: cgc.coverage?.name,
                          value: cgc.id,
                          disabled: cgc.coverage?.required
                        }))
                      "
                      mode="multiple"
                    />
                  </a-form-item>
                </a-col>
              </template>
            </a-row>
          </template>
        </a-card>
      </a-col>

      <a-col :xs="24">
        <a-card>
          <a-typography-title class="text-gray-8" :level="4">
            Mensaje a compañía aseguradora
          </a-typography-title>

          <a-row :gutter="[8, 8]">
            <a-col :xs="24" :md="12">
              <a-form-item
                name="message"
                label="Escribe un mensaje para la compañía aseguradora"
                :rules="{ required: true }"
              >
                <a-textarea
                  v-model:value="formData.message"
                  placeholder="Escribe aquí..."
                  :rows="8"
                  :maxlength="3000"
                />
              </a-form-item>
            </a-col>
            <a-col :xs="24" :md="12">
              <a-form-item name="optionalFiles" label="Archivos opcionales">
                <a-upload
                  :file-list="formData.optionalFiles"
                  :multiple="true"
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
                  :remove="handleRemoveOptional"
                  :before-upload="beforeUploadOptional"
                >
                  <a-button>
                    <upload-outlined></upload-outlined>
                    Subir archivo
                  </a-button>
                </a-upload>
              </a-form-item>
            </a-col>
          </a-row>
        </a-card>
      </a-col>
    </a-row>

    <a-row type="flex" class="justify-content-end mt-3">
      <a-col>
        <a-button
          size="large"
          class="px-4"
          type="primary"
          @click="formSubmit"
          block
        >
          Siguiente
        </a-button>
      </a-col>
    </a-row>
  </a-form>

  <TransferCreationResumeModal
    ref="resumeModalRef"
    :loading="resumeModalLoading"
    @createTransfer="createTransfer"
  />

  <TransferCreationResumeSuccessModal ref="resumeSuccessModalRef" />
</template>

<script setup>
import { ref, reactive, computed, watch, onMounted } from 'vue';
import TransferCreationResumeModal from './transfer-creation-resume-modal.vue';
import TransferCreationResumeSuccessModal from './transfer-creation-resume-success-modal.vue';
import PageHeader from '@/components/page-header.vue';
import InputRut from '@/bewell-components/form-controls/input-rut.vue';
import SelectOrdered from '@/bewell-components/form-controls/select-ordered.vue';
import { UploadOutlined } from '@ant-design/icons-vue';
import notifications from '@/utils/notifications';
import { useStore } from 'vuex';
import {
  EMPLOYEES_FETCH_EMPLOYEE,
  EMPLOYEES_GET_LOADING,
  EMPLOYEES_GET_ERROR,
  COMPANIES_FETCH_COMPANIES,
  COMPANIES_GET_ERROR,
  INSURANCES_FETCH_INSURANCES,
  INSURANCES_GET_ERROR,
  POLICIES_FETCH_POLICIES,
  POLICIES_FETCH_GROUPS,
  POLICIES_GET_ERROR,
  COVERAGE_GROUP_COMPANIES_FETCH_COVERAGE_GROUP_COMPANIES,
  COVERAGE_GROUP_COMPANIES_GET_ERROR,
  GET_LOGGED_USER,
  POST_TICKET_TRANSFER
} from '@/store/types';
import rutHelper from '@/utils/Ruts';

const DATE_FORMAT = 'DD-MM-YYYY';
const DEFAULT_MESSAGE =
  'A través del presente correo solicitamos el siguiente traspaso:';
const ROUTES = [
  {
    path: '/traspasos',
    breadcrumbName: 'Traspasos'
  },
  {
    breadcrumbName: 'Formulario'
  }
];

const store = useStore();

const brokerId = store.getters[GET_LOGGED_USER].id;

const fetchingEmployee = computed(
  () => store.getters[EMPLOYEES_GET_LOADING].fetchEmployee
);
const employeeError = computed(
  () => store.getters[EMPLOYEES_GET_ERROR].fetchEmployee
);

const fetchingOriginCompanies = ref(false);
const originCompaniesError = ref(null);
const originCompanies = ref([]);
const originCompanyOptions = computed(() => {
  return originCompanies.value.map((company) => ({
    label: company.businessName,
    value: company.id
  }));
});

const fetchingOriginInsurances = ref(false);
const originInsuranceCompaniesError = ref(null);
const originInsuranceCompanies = ref([]);
const originInsuranceOptions = computed(() => {
  return originInsuranceCompanies.value.map((insurance) => ({
    label: insurance.businessName,
    value: insurance.id
  }));
});

const fetchingOriginPolicies = ref(false);
const originPoliciesError = ref(null);
const originPolicies = ref([]);
const originPolicyOptions = computed(() => {
  return originPolicies.value.map((policy) => ({
    label: policy.numberPolicy,
    value: policy.id
  }));
});

const fetchingOriginGroups = ref(false);

const fetchingDestinyCompanies = ref(false);
const destinyCompaniesError = ref(null);
const destinyCompanies = ref([]);
const destinyCompanyOptions = computed(() => {
  return destinyCompanies.value.map((company) => ({
    label: company.businessName,
    value: company.id
  }));
});

const fetchingDestinyInsuranceCompanies = ref(false);
const destinyInsuranceCompaniesError = ref(null);
const destinyInsuranceCompanies = ref([]);
const destinyInsuranceCompanyOptions = computed(() => {
  return destinyInsuranceCompanies.value.map((insurance) => ({
    label: insurance.businessName,
    value: insurance.id
  }));
});

const fetchingDestinyPolicies = ref(false);
const destinyPoliciesError = ref(null);
const destinyPolicies = ref([]);
const destinyPolicyOptions = computed(() => {
  return destinyPolicies.value.map((policy) => ({
    label: policy.numberPolicy,
    value: policy.id
  }));
});

const fetchingDestinyGroups = ref(false);
const fetchingDestinyCoverages = ref(false);

const formRef = ref();
const formData = reactive({
  formType: '1',
  startDate: null,

  rut: null,
  fullname: '',
  employeeId: null,

  originCompanyId: null,
  originInsuranceCompanyId: null,
  originPolicyIds: [],
  originPolicies: [],

  destinyCompanyId: null,
  destinyInsuranceCompanyId: null,
  destinyPolicyIds: [],
  destinyPolicies: [],

  message: DEFAULT_MESSAGE,
  optionalFiles: []
});

let debounceTimer = null;

watch(
  () => formData.rut,
  async (newRut) => {
    formData.fullname = '';
    formData.employeeId = null;
    formData.originCompanyId = null;
    formData.originInsuranceCompanyId = null;
    formData.originPolicyIds = [];
    formData.originPolicies.forEach((p) => {
      p.originGroupIds = [];
    });
    formData.originPolicies = [];

    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }

    debounceTimer = setTimeout(async () => {
      if (!fetchingEmployee.value && newRut && rutHelper.validateRut(newRut)) {
        const employee = await store.dispatch(EMPLOYEES_FETCH_EMPLOYEE, {
          rut: rutHelper.normalizeRut(newRut)
        });

        if (employee) {
          formData.fullname = employee.fullName;
          formData.employeeId = employee.id;
          fetchingOriginCompanies.value = true;
          originCompaniesError.value = null;
          const companies = await store.dispatch(COMPANIES_FETCH_COMPANIES, {
            employeeId: employee.id,
            employeeStatus: 'ACTIVE'
          });
          fetchingOriginCompanies.value = false;
          originCompaniesError.value =
            store.getters[COMPANIES_GET_ERROR].fetchCompanies;
          originCompanies.value = companies;
        }
      }
    }, 500);
  }
);

watch(
  () => formData.originCompanyId,
  async (newOriginCompanyId) => {
    if (newOriginCompanyId) {
      formData.originInsuranceCompanyId = null;
      formData.originPolicyIds = [];
      formData.originPolicies.forEach((p) => {
        p.originGroupIds = [];
      });
      formData.originPolicies = [];

      fetchingOriginInsurances.value = true;
      originInsuranceCompaniesError.value = null;
      const insurances = await store.dispatch(INSURANCES_FETCH_INSURANCES, {
        companyIds: [newOriginCompanyId],
        employeeId: formData.employeeId,
        employeeStatus: 'ACTIVE'
      });
      fetchingOriginInsurances.value = false;
      originInsuranceCompaniesError.value =
        store.getters[INSURANCES_GET_ERROR].fetchInsurances;
      originInsuranceCompanies.value = insurances;
    }
  }
);

watch(
  () => formData.originInsuranceCompanyId,
  async (newOriginInsuranceCompanyId) => {
    if (newOriginInsuranceCompanyId) {
      formData.originPolicyIds = [];
      formData.originPolicies.forEach((p) => {
        p.originGroupIds = [];
      });
      formData.originPolicies = [];

      fetchingOriginPolicies.value = true;
      originPoliciesError.value = null;
      const policies = await store.dispatch(POLICIES_FETCH_POLICIES, {
        companyIds: [formData.originCompanyId],
        insuranceCompanyId: newOriginInsuranceCompanyId,
        employeeId: formData.employeeId,
        employeeStatus: 'ACTIVE'
      });
      fetchingOriginPolicies.value = false;
      const err = store.getters[POLICIES_GET_ERROR].fetchGroups;
      originPoliciesError.value = err;
      if (err) return;

      for (const policy of policies) {
        fetchingOriginGroups.value = true;
        originPoliciesError.value = null;
        const groups = await store.dispatch(POLICIES_FETCH_GROUPS, {
          policyId: policy.id,
          employeeId: formData.employeeId,
          employeeStatus: 'ACTIVE'
        });
        policy.originGroups = [];
        policy.originGroupIds = [];
        for (const group of groups) {
          policy.originGroups.push(group);
        }
        fetchingOriginGroups.value = false;
        const err = store.getters[POLICIES_GET_ERROR].fetchGroups;
        originPoliciesError.value = err;
        if (err) return;
      }

      originPolicies.value = policies;
    }
  }
);

watch(
  () => formData.originPolicyIds,
  (newOriginPolicyIds) => {
    for (const newOriginPolicyId of newOriginPolicyIds) {
      const existingPolicyIndex = formData.originPolicies.findIndex(
        (p) => p.id === newOriginPolicyId
      );
      if (existingPolicyIndex !== -1) {
        continue; // Skip if the policy is already added
      }
      const policy = originPolicies.value.find(
        (p) => p.id === newOriginPolicyId
      );
      policy.originGroupIds = [];
      formData.originPolicies.push(policy);
    }
    formData.originPolicies = formData.originPolicies.filter((p) =>
      newOriginPolicyIds.includes(p.id)
    );
  }
);

watch(
  () => formData.destinyCompanyId,
  async (newDestinyCompanyId) => {
    if (newDestinyCompanyId) {
      formData.destinyInsuranceCompanyId = null;
      formData.destinyPolicyIds = [];
      formData.destinyPolicies = [];

      fetchingDestinyInsuranceCompanies.value = true;
      destinyInsuranceCompaniesError.value = null;
      const insurances = await store.dispatch(INSURANCES_FETCH_INSURANCES, {
        companyIds: [newDestinyCompanyId]
      });
      fetchingDestinyInsuranceCompanies.value = false;
      destinyInsuranceCompaniesError.value =
        store.getters[INSURANCES_GET_ERROR].fetchInsurances;
      destinyInsuranceCompanies.value = insurances;
    }
  }
);

watch(
  () => formData.destinyInsuranceCompanyId,
  async (newDestinyInsuranceCompanyId) => {
    if (newDestinyInsuranceCompanyId) {
      formData.destinyPolicyIds = [];
      formData.destinyPolicies = [];

      fetchingDestinyPolicies.value = true;
      destinyPoliciesError.value = null;
      const policies = await store.dispatch(POLICIES_FETCH_POLICIES, {
        companyIds: [formData.destinyCompanyId],
        insuranceCompanyId: newDestinyInsuranceCompanyId,
        status: 'Vigente'
      });
      const err = store.getters[POLICIES_GET_ERROR].fetchGroups;
      destinyPoliciesError.value = err;
      if (err) return;

      for (const policy of policies) {
        fetchingDestinyGroups.value = true;
        destinyPoliciesError.value = null;
        const groups = await store.dispatch(POLICIES_FETCH_GROUPS, {
          policyId: policy.id
        });
        policy.destinyGroups = [];
        policy.destinyGroupIds = [];
        for (const group of groups) {
          fetchingDestinyCoverages.value = true;
          destinyPoliciesError.value = null;
          const coverageGroupCompanies = await store.dispatch(
            COVERAGE_GROUP_COMPANIES_FETCH_COVERAGE_GROUP_COMPANIES,
            {
              policyId: policy.id,
              groupCompanyId: group.id
            }
          );
          fetchingDestinyCoverages.value = false;
          const err =
            store.getters[COVERAGE_GROUP_COMPANIES_GET_ERROR]
              .fetchCoverageGroupCompanies;
          destinyPoliciesError.value = err;
          if (err) return;
          group.destinyCoverages = coverageGroupCompanies;
          group.destinyCoverageIds =
            coverageGroupCompanies
              ?.filter((cgc) => cgc.coverage?.required)
              ?.map((cgc) => cgc.id) || [];
          policy.destinyGroups.push(group);
        }
        fetchingDestinyGroups.value = false;
        const err = store.getters[POLICIES_GET_ERROR].fetchGroups;
        destinyPoliciesError.value = err;
        if (err) return;
      }

      destinyPolicies.value = policies;
      fetchingDestinyPolicies.value = false;
    }
  }
);

watch(
  () => formData.destinyPolicyIds,
  (newDestinyPolicyIds) => {
    for (const newDestinyPolicyId of newDestinyPolicyIds) {
      const existingPolicyIndex = formData.destinyPolicies.findIndex(
        (p) => p.id === newDestinyPolicyId
      );
      if (existingPolicyIndex !== -1) {
        continue; // Skip if the policy is already added
      }
      const policy = destinyPolicies.value.find(
        (p) => p.id === newDestinyPolicyId
      );
      policy.destinyGroupIds = [];
      formData.destinyPolicies.push({ ...policy });
    }

    formData.destinyPolicies = formData.destinyPolicies.filter((p) => {
      const isIncluded = newDestinyPolicyIds.includes(p.id);
      if (!isIncluded) {
        p.destinyGroupIds = [];
        p.destinyGroups.forEach(
          (dg) =>
            (dg.destinyCoverageIds =
              dg.destinyCoverages
                ?.filter((cgc) => cgc.coverage?.required)
                ?.map((cgc) => cgc.id) || [])
        );
      }
      return isIncluded;
    });
  }
);

const changeDestinyGroupCompany = (newDestinyGroupIds, destinyGroups) => {
  destinyGroups.forEach((dg) => {
    if (!newDestinyGroupIds.includes(dg.id)) {
      dg.destinyCoverageIds =
        dg.destinyCoverages
          ?.filter((cgc) => cgc.coverage?.required)
          ?.map((cgc) => cgc.id) || [];
    }
  });
};

onMounted(async () => {
  fetchingDestinyCompanies.value = true;
  destinyCompaniesError.value = null;
  const companies = await store.dispatch(COMPANIES_FETCH_COMPANIES, {});
  fetchingDestinyCompanies.value = false;
  destinyCompaniesError.value =
    store.getters[COMPANIES_GET_ERROR].fetchCompanies;
  destinyCompanies.value = companies;
});

const handleRemoveOptional = (file) => {
  formData.optionalFiles = formData.optionalFiles.filter((f) => f !== file);
};

const beforeUploadOptional = (file) => {
  if (formData.optionalFiles.length < 5) {
    const fileSize = file.size / 1024 / 1024;
    if (fileSize > 5) {
      notifications('error', 'Error', 'El archivo excede el peso máximo 5MB');
    } else {
      formData.optionalFiles = [...formData.optionalFiles, file];
    }
  } else {
    notifications('error', 'Error', 'Máximo archivos a subir es 5');
  }
  return false;
};

// * Resume modal
const resumeModalRef = ref();
const resumeModalLoading = ref(false);
const openResumeModal = (values) => {
  resumeModalRef.value.openModal(values);
};

// * Resume success modal
const resumeSuccessModalRef = ref();

const createTransfer = async () => {
  resumeModalLoading.value = true;
  try {
    const formBody = new FormData();
    formBody.append('rut', rutHelper.normalizeRut(formData.rut));
    formBody.append('brokerId', brokerId);
    formData.originPolicies.forEach((policy) => {
      policy.originGroups.forEach((group) => {
        if (!policy.originGroupIds.includes(group.id)) {
          return;
        }
        formBody.append(
          'policiesGroupCompanies[]',
          JSON.stringify({
            policyId: policy.id,
            groupCompanyId: group.id
          })
        );
      });
    });
    formData.destinyPolicies.forEach((p) => {
      if (!formData.destinyPolicyIds.includes(p.id)) {
        return;
      }
      p.destinyGroups.forEach((g) => {
        if (!p.destinyGroupIds.includes(g.id)) {
          return;
        }
        g.destinyCoverages.forEach((cgc) => {
          if (!g.destinyCoverageIds.includes(cgc.id)) {
            return;
          }
          formBody.append('newCoverageGroupCompanyIds[]', cgc.id);
        });
      });
    });
    formBody.append('transferDate', formData.startDate.format());
    formData.optionalFiles.forEach((of) => {
      formBody.append('optionalFiles', of);
    });
    formBody.append('message', formData.message);

    await store.dispatch(POST_TICKET_TRANSFER, formBody).then((resp) => {
      const { id } = resp.data;
      resumeModalRef.value.closeModal();
      resumeSuccessModalRef.value.openSuccessModal(id);
      formRef.value.resetFields();
    });
  } catch (error) {
    console.log(error);
    const errMsg =
      error.response?.data?.message ||
      'Ha ocurrido un error al crear el traspaso.';
    notifications('error', 'Error', errMsg);
  }
  resumeModalLoading.value = false;
};

const formSubmit = () => {
  formRef.value
    .validate()
    .then(() => {
      openResumeModal({
        requestStart: formData.startDate,
        name: formData.fullname,
        rut: formData.rut,
        originCompany: originCompanies.value.find(
          (company) => company.id === formData.originCompanyId
        ),
        originInsuranceCompany: originInsuranceCompanies.value.find(
          (insurance) => insurance.id === formData.originInsuranceCompanyId
        ),
        originPolicies: formData.originPolicies.map((p) => ({
          ...p,
          originGroups: p.originGroups.filter((g) =>
            p.originGroupIds.includes(g.id)
          )
        })),
        destinyCompany: destinyCompanies.value.find(
          (company) => company.id === formData.destinyCompanyId
        ),
        destinyInsuranceCompany: destinyInsuranceCompanies.value.find(
          (insurance) => insurance.id === formData.destinyInsuranceCompanyId
        ),
        destinyPolicies: formData.destinyPolicies
          .filter((p) => formData.destinyPolicyIds.includes(p.id))
          .map((p) => ({
            ...p,
            destinyGroups: p.destinyGroups
              .filter((g) => p.destinyGroupIds.includes(g.id))
              .map((g) => ({
                ...g,
                destinyCoverages: g.destinyCoverages.filter((cgc) =>
                  g.destinyCoverageIds.includes(cgc.id)
                )
              }))
          })),
        optionalFiles: formData.optionalFiles,
        message: formData.message
      });
    })
    .catch((err) => {
      console.log(err);
    });
};
</script>
